import {
  toWorkspaceItemsSearchParams,
  workspaceItemsQueryShape,
} from '@bower/shared/schemas/workspaceItems.schema';
import type {
  ApiResponse,
  BulkMoveRequest,
  BulkMoveResult,
  FileKindCounts,
  PaginatedResponse,
  WorkspaceItem,
  WorkspaceItemPreview,
  WorkspaceItemsPageMeta,
  WorkspaceItemsQuery,
  WorkspaceItemThumbnails,
} from '@bower/shared';
import type { HttpClient } from './http-client.types';

/**
 * The unified workspace view (folders + files). Backed by
 * `/api/workspace/items`; the query is serialized with the same shared
 * contract the route validates, so the two cannot drift apart.
 */

export type WorkspaceItemsPage = PaginatedResponse<WorkspaceItem> & WorkspaceItemsPageMeta;

const QUERY_DEFAULTS: WorkspaceItemsQuery = workspaceItemsQueryShape.parse({});

/**
 * One page. Fields left out take the route defaults (and are not sent).
 * `headers` is for the boot prefetch only (`lib/bootItemsPrefetch.ts`), which
 * runs before the app has installed its token and workspace header.
 */
export function listWorkspaceItems(
  http: HttpClient,
  query: Partial<WorkspaceItemsQuery>,
  signal?: AbortSignal,
  headers?: Record<string, string>,
): Promise<ApiResponse<WorkspaceItemsPage>> {
  const search = toWorkspaceItemsSearchParams({ ...QUERY_DEFAULTS, ...query }).toString();
  const config = { ...(signal && { signal }), ...(headers && { headers }) };
  return http.get<WorkspaceItemsPage>(
    search ? `/workspace/items?${search}` : '/workspace/items',
    Object.keys(config).length > 0 ? config : undefined,
  );
}

/**
 * The Type menu's per-kind counts for a list: send the list's own query; the
 * server ignores its Type filter, sort, cursor and limit.
 */
export function countWorkspaceItemsByKind(
  http: HttpClient,
  query: Partial<WorkspaceItemsQuery>,
): Promise<ApiResponse<{ counts: FileKindCounts }>> {
  const search = toWorkspaceItemsSearchParams({ ...QUERY_DEFAULTS, ...query }).toString();
  return http.get<{ counts: FileKindCounts }>(
    search ? `/workspace/items/kind-counts?${search}` : '/workspace/items/kind-counts',
  );
}

export function getWorkspaceItemPreview(
  http: HttpClient,
  id: string,
): Promise<ApiResponse<WorkspaceItemPreview>> {
  return http.get<WorkspaceItemPreview>(`/workspace/items/${id}/preview`);
}

/** Signed thumbnails for up to one page of file ids; ids with none are absent. */
export function getWorkspaceItemThumbnails(
  http: HttpClient,
  ids: readonly string[],
  signal?: AbortSignal,
): Promise<ApiResponse<WorkspaceItemThumbnails>> {
  return http.get<WorkspaceItemThumbnails>(
    `/workspace/items/thumbnails?ids=${ids.join(',')}`,
    signal ? { signal } : undefined,
  );
}

/**
 * All-or-nothing: a refusal moves nothing, and its `error.details.items`
 * names each failing item with its reason (`BulkMoveItemFailure`).
 */
export function bulkMoveWorkspaceItems(
  http: HttpClient,
  body: BulkMoveRequest,
): Promise<ApiResponse<BulkMoveResult>> {
  return http.post<BulkMoveResult>('/workspace/items/bulk-move', body);
}
